import BURJackpotsModel from "../../models/BURJackpotsModel";
import { Jackpot } from "../../types/IBURJackpotsModel";
import { createJackpotCell, createSymbolCell } from "./BURHistoryCells";
import {
    createElement,
    createTable,
    HistoryCell,
    HISTORY_CSS
} from "./BURHistoryDom";
import BURHistoryFormatter from "./BURHistoryFormatter";
import { getSpinJackpotWins } from "./BURHistoryStats";
import { historyText } from "./BURHistoryTexts";
import {
    IHistoryCascadeStep,
    IHistoryJackpotWin,
    IHistorySpin
} from "./BURHistoryTypes";

// Attribute of a clusters table row with the cascade step it belongs to
export const CASCADE_ATTRIBUTE = "data-cascade";

// A text is never scaled below this fraction of its normal size
const MIN_TEXT_SCALE = 0.4;

/**
 * The font size (px) at which the text of the cell fits it, or null when it already fits or the cell is not shown
 * (no width yet). The text is as wide as its font size is big, so the width of the content tells the size needed.
 * The cell has to be at its normal font size.
 */
function getFittingSize(cell: HTMLElement): number | null {
    if (cell.scrollWidth <= cell.clientWidth) {
        return null;
    }
    const style = getComputedStyle(cell);
    const padding =
        (parseFloat(style.paddingLeft) || 0) +
        (parseFloat(style.paddingRight) || 0);
    const available = cell.clientWidth - padding;
    const needed = cell.scrollWidth - padding;
    const size = parseFloat(style.fontSize);
    if (!(available > 0 && needed > 0 && size > 0)) {
        return null;
    }
    // Rounded down, so that the rounding does not leave it a pixel too wide
    return Math.max(
        size * MIN_TEXT_SCALE,
        Math.floor((size * available * 10) / needed) / 10
    );
}

/**
 * Scales the texts of the wins tables under the root down until they fit their cells: the columns are fixed, but a
 * long amount (last column) or header in a narrow cell would otherwise spill out of the table. The headers get one
 * size, the smallest any of them needs, so they look alike; each amount gets its own. Texts that fit, or are not
 * shown, keep their normal size. To be called when the tables are shown and when their width changes.
 */
export function fitTableTexts(root: ParentNode): void {
    const tables = Array.from(
        root.querySelectorAll(`.${HISTORY_CSS.winsTable}`)
    );
    const headers = tables.map((table) =>
        Array.from(table.querySelectorAll<HTMLElement>("th"))
    );
    const cells = tables.map((table) =>
        Array.from(table.querySelectorAll<HTMLElement>("td:last-child"))
    );

    // Everything is measured at its normal size and the sizes are applied after that, so the browser lays the page
    // out once, instead of after every change of a font size
    headers.flat().forEach((header) => (header.style.fontSize = ""));
    cells.flat().forEach((cell) => (cell.style.fontSize = ""));

    const headerSizes = headers.map((tableHeaders) => {
        const sizes = tableHeaders
            .map(getFittingSize)
            .filter((size): size is number => size !== null);
        return sizes.length ? Math.min(...sizes) : null;
    });
    const cellSizes = cells.map((tableCells) => tableCells.map(getFittingSize));

    tables.forEach((_, index) => {
        if (headerSizes[index] !== null) {
            headers[index].forEach(
                (header) => (header.style.fontSize = `${headerSizes[index]}px`)
            );
        }
        cells[index].forEach((cell, cellIndex) => {
            const size = cellSizes[index][cellIndex];
            if (size !== null) {
                cell.style.fontSize = `${size}px`;
            }
        });
    });
}

/**
 * How many jackpots of the tier the row pays. The round data only has the credits, which are
 * count * tier value * bet (the tier value comes from the init jackpots config, as for the game's own win).
 */
export function getJackpotCount(
    jackpot: IHistoryJackpotWin,
    bet: number
): string {
    const value =
        BURJackpotsModel.jackpots_config?.[
            jackpot.tier.toUpperCase() as Jackpot
        ]?.value;
    if (!value || !bet) {
        return "-";
    }
    const count = jackpot.credits / (value * bet);
    const rounded = Math.round(count);
    return rounded >= 1 && Math.abs(count - rounded) < 0.01
        ? `${rounded}`
        : "-";
}

/**
 * "Wins" with the clusters table and/or the jackpots table, or nothing (an empty element) when the spin won nothing.
 * The jackpots table is only there when jackpots are enabled (free spins whose combo includes Yellow)
 * and at least one jackpot was won in the spin.
 */
export function createWinsView(
    spin: IHistorySpin,
    jackpotsEnabled: boolean,
    formatter: BURHistoryFormatter
): HTMLElement {
    const jackpotWins = getSpinJackpotWins(spin);
    const clusters = createClustersTable(spin.cascadeSteps || [], formatter);
    const jackpots = jackpotsEnabled
        ? createJackpotsTable(jackpotWins, formatter)
        : null;

    const view = createElement("div", HISTORY_CSS.spinWins);
    // A spin without wins shows nothing under its grid
    if (!clusters && jackpotWins.length === 0) {
        return view;
    }
    view.appendChild(
        createElement("div", HISTORY_CSS.subtitle, historyText("wins"))
    );
    if (clusters) {
        view.appendChild(clusters);
    }
    if (jackpots) {
        view.appendChild(jackpots);
    }
    return view;
}

// Clusters are numbered through the whole spin, across its cascades
function createClustersTable(
    cascadeSteps: IHistoryCascadeStep[],
    formatter: BURHistoryFormatter
): HTMLTableElement | null {
    const rows: HistoryCell[][] = [];
    // The cascade of each row, to tag the rows with
    const cascades: number[] = [];
    cascadeSteps.forEach((step) =>
        step.clusters.forEach((cluster) => {
            rows.push([
                `${rows.length + 1}`,
                `${step.cascadeIndex + 1}`,
                createSymbolCell(cluster.symbol),
                `${cluster.size}`,
                formatter.money(cluster.creditsWon)
            ]);
            cascades.push(step.cascadeIndex);
        })
    );
    if (!rows.length) {
        return null;
    }

    const table = createTable(
        [
            historyText("clusterId"),
            historyText("cascadeId"),
            historyText("symbol"),
            historyText("size"),
            historyText("win")
        ],
        rows,
        HISTORY_CSS.winsTable
    );
    // Row i of the body is the i-th cluster: tag it with its cascade for the grid to highlight
    Array.from(table.tBodies[0].rows).forEach((row, i) =>
        row.setAttribute(CASCADE_ATTRIBUTE, `${cascades[i]}`)
    );
    return table;
}

// One row per jackpot tier won in the spin, totalled over the whole spin. Tiers are in the order of BURJackpotsModel
// (by value, MINI first).
function createJackpotsTable(
    jackpotWins: IHistoryJackpotWin[],
    formatter: BURHistoryFormatter
): HTMLTableElement | null {
    const totals = new Map<string, number>();
    jackpotWins.forEach((jackpot) =>
        totals.set(
            jackpot.tier,
            (totals.get(jackpot.tier) || 0) + jackpot.credits
        )
    );
    // No table when nothing was won
    if (!totals.size) {
        return null;
    }
    const rows: HistoryCell[][] = BURJackpotsModel.jackpot_tiers
        .filter((tier) => totals.has(tier))
        .map((tier, index) => {
            const credits = totals.get(tier)!;
            return [
                `${index + 1}`,
                createJackpotCell(tier),
                getJackpotCount({ tier, credits }, formatter.bet),
                formatter.money(credits)
            ];
        });

    const table = createTable(
        [
            historyText("jackpotId"),
            historyText("tier"),
            historyText("size"),
            historyText("win")
        ],
        rows,
        HISTORY_CSS.winsTable
    );
    table.classList.add(HISTORY_CSS.jackpotsTable);
    return table;
}
